import { FileText, Copy, Loader2 } from 'lucide-react';
import { TreeView } from '@/components/ui/tree-view';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

interface NodeDataPanelProps {
  title: string;
  data: unknown;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function NodeDataPanel({
  title,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
}: NodeDataPanelProps) {
  // If data is a JSON string, parse it for proper TreeView rendering
  const parsedData = (() => {
    if (data === undefined || data === null) return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data; // Return as-is if not valid JSON
      }
    }
    return data;
  })();

  const hasData = parsedData !== undefined && parsedData !== null;
  const dataStr = hasData ? JSON.stringify(parsedData) : '';
  const isLargeData = dataStr.length > 100000; // 100KB

  const handleCopy = () => {
    if (hasData) {
      navigator.clipboard.writeText(JSON.stringify(parsedData, null, 2));
      toast.success('Copied to clipboard');
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ contain: 'strict' }}>
      {/* Panel Header */}
      <div className="border-b px-4 py-2 flex-shrink-0 flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4" />
          {title}
        </h3>
        {hasData && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <Copy className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Panel Content - Single scroll container with visible scrollbar */}
      <ScrollArea className="flex-1 min-h-0" style={{ contain: 'strict' }}>
        <div className="p-4 overflow-hidden" style={{ contain: 'layout style' }}>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading data...
              </div>
            </div>
          ) : !hasData ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center text-muted-foreground text-sm">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>{emptyMessage}</p>
              </div>
            </div>
          ) : isLargeData ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Large data ({(dataStr.length / 1024).toFixed(1)} KB) - showing raw JSON</span>
              </div>
              <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-muted/30 rounded p-3 border">
                {dataStr.substring(0, 10000)}
                {dataStr.length > 10000 && '\n\n... (truncated for display)'}
              </pre>
            </div>
          ) : (
            <div className="bg-muted/30 rounded border p-3 break-words overflow-hidden" style={{ contain: 'layout style' }}>
              <TreeView
                data={parsedData}
                defaultExpanded={2}
                showTypes={true}
                showCopyButtons={true}
                maxDepth={6}
                className="text-sm [&_*]:break-words [&_*]:overflow-wrap-anywhere [&_*]:max-w-full"
              />
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
